
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CooksyLogo } from "@/components/CooksyLogo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Home, Heart, Utensils, Inbox, User, Menu, Settings, Package, Building2, BarChart2, Shield, Flag, ChartBar, ArrowLeft, Mail } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CartButton } from "@/components/CartButton";

interface MobileWebNavProps {
  isSellerRoute: boolean;
}

export const MobileWebNav = ({
  isSellerRoute
}: MobileWebNavProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const foodieMainRoutes = ['/', '/favorites', '/meals', '/inbox', '/profile'];
  const sellerMainRoutes = ['/seller/meals', '/seller/orders', '/seller/business', '/seller/inbox', '/seller/profile'];
  const adminMainRoutes = ['/admin/dashboard', '/admin/verifications', '/admin/disputes', '/admin/reporting', '/admin/email-templates', '/admin/settings'];
  const isMainRoute = isAdminRoute ? adminMainRoutes.includes(location.pathname) : isSellerRoute ? sellerMainRoutes.includes(location.pathname) : foodieMainRoutes.includes(location.pathname);
  
  const { data: unreadCount } = useQuery({
    queryKey: ["unread-messages", isSellerRoute],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      
      const { data, error } = await supabase
        .from("conversations")
        .select("unread_count")
        .eq(isSellerRoute ? "cook_id" : "foodie_id", user.id);
      
      if (error) return 0;
      return data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
    },
    refetchInterval: 5000
  });
  
  const renderAdminNavItems = () => <>
      <Link to="/admin/dashboard" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/admin/dashboard' ? 'text-primary' : 'text-muted-foreground'}`}>
        <BarChart2 className="h-5 w-5" />
        <span>Dashboard</span>
      </Link>
      <Link to="/admin/verifications" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/admin/verifications' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Shield className="h-5 w-5" />
        <span>Verifications</span>
      </Link>
      <Link to="/admin/disputes" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/admin/disputes' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Flag className="h-5 w-5" />
        <span>Disputes</span>
      </Link>
      <Link to="/admin/reporting" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/admin/reporting' ? 'text-primary' : 'text-muted-foreground'}`}>
        <ChartBar className="h-5 w-5" />
        <span>Reporting</span>
      </Link>
      <Link to="/admin/email-templates" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/admin/email-templates' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Mail className="h-5 w-5" />
        <span>Email Templates</span>
      </Link>
      <Link to="/admin/settings" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/admin/settings' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Settings className="h-5 w-5" />
        <span>Settings</span>
      </Link>
    </>;
    
  const renderFoodieNavItems = () => <>
      <Link to="/" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Home className="h-5 w-5" />
        <span>Home</span>
      </Link>
      <Link to="/favorites" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/favorites' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Heart className="h-5 w-5" />
        <span>Favorites</span>
      </Link>
      <Link to="/meals" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/meals' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Utensils className="h-5 w-5" />
        <span>Orders</span>
      </Link>
      <Link to="/inbox" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/inbox' ? 'text-primary' : 'text-muted-foreground'}`}>
        <div className="relative">
          <Inbox className="h-5 w-5" />
          {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
              {unreadCount}
            </span>}
        </div>
        <span>Inbox</span>
      </Link>
      <Link to="/profile" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/profile' ? 'text-primary' : 'text-muted-foreground'}`}>
        <User className="h-5 w-5" />
        <span>Profile</span>
      </Link>
    </>;
    
  const renderSellerNavItems = () => <>
      <Link to="/seller/meals" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/seller/meals' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Utensils className="h-5 w-5" />
        <span>My Meals</span>
      </Link>
      <Link to="/seller/orders" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/seller/orders' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Package className="h-5 w-5" />
        <span>Orders</span>
      </Link>
      <Link to="/seller/business" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/seller/business' ? 'text-primary' : 'text-muted-foreground'}`}>
        <Building2 className="h-5 w-5" />
        <span>Business</span>
      </Link>
      <Link to="/seller/inbox" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/seller/inbox' ? 'text-primary' : 'text-muted-foreground'}`}>
        <div className="relative">
          <Inbox className="h-5 w-5" />
          {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
              {unreadCount}
            </span>}
        </div>
        <span>Inbox</span>
      </Link>
      <Link to="/seller/profile" onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${location.pathname === '/seller/profile' ? 'text-primary' : 'text-muted-foreground'}`}>
        <User className="h-5 w-5" />
        <span>Profile</span>
      </Link>
    </>;
    
  return <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="container mx-auto px-4">
        <div className="h-16 flex items-center">
          <div className="flex-1 flex items-center">
            {isMainRoute ? <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px]" autoFocus={false}>
                  <nav className="flex flex-col gap-4 mt-8">
                    {isAdminRoute ? renderAdminNavItems() : isSellerRoute ? renderSellerNavItems() : renderFoodieNavItems()}
                  </nav>
                </SheetContent>
              </Sheet> : <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>}
          </div>
          
          <div className="flex-auto flex justify-center">
            <CooksyLogo />
          </div>
          
          <div className="flex-1 flex justify-end">
            {!isSellerRoute && !isAdminRoute && <CartButton />}
          </div>
        </div>
      </div>
    </header>;
};
